import { useState } from "react";
import { Link } from "react-router-dom";
import { Users, Briefcase, ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useConfig } from "@/hooks/useConfig";
import { euro } from "@/lib/api";

const FILTERS = ["Tous", "Berline", "SUV", "Van"];

export default function Fleet() {
  const { config } = useConfig();
  const vehicles = config?.vehicles || [];
  const [filter, setFilter] = useState("Tous");

  const shown = filter === "Tous" ? vehicles : vehicles.filter((v) => v.category === filter);

  return (
    <div data-testid="fleet-page" className="pt-32">
      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal>
          <p className="text-gold text-xs tracking-[0.4em] uppercase">Véhicules</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-white">Notre Flotte</h1>
          <p className="mt-5 max-w-2xl text-white/60">
            Une sélection de véhicules récents, haut de gamme et méticuleusement entretenus pour chaque occasion.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-3">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`fleet-filter-${f.toLowerCase()}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-5 py-2 text-sm transition-colors duration-200 ${
                filter === f ? "border-gold bg-gold text-black" : "border-white/15 text-white/70 hover:border-gold/50 hover:text-white"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-10 py-14 lg:py-20 space-y-8">
        {shown.map((v, i) => (
          <Reveal key={v.id} delay={(i % 3) * 0.08}>
            <div
              data-testid={`fleet-card-${v.id}`}
              className="grid lg:grid-cols-2 gap-0 overflow-hidden rounded-2xl border border-white/10 bg-panel group"
            >
              <div className={`overflow-hidden ${i % 2 ? "lg:order-2" : ""}`}>
                <img src={v.image} alt={v.name} className="h-72 lg:h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-8 lg:p-12 flex flex-col">
                <span className="text-xs text-gold tracking-[0.3em] uppercase">{v.category}</span>
                <h3 className="mt-3 font-serif text-3xl text-white">{v.name}</h3>
                <p className="text-white/50 text-sm mt-1">{v.model}</p>

                <div className="mt-6 flex gap-8 text-sm text-white/70">
                  <span className="flex items-center gap-2"><Users size={16} className="text-gold" /> {v.seats} passagers</span>
                  <span className="flex items-center gap-2"><Briefcase size={16} className="text-gold" /> {v.luggage} bagages</span>
                </div>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  {v.features.map((f) => (
                    <span key={f} className="flex items-center gap-2 text-sm text-white/60">
                      <Check size={14} className="text-gold shrink-0" /> {f}
                    </span>
                  ))}
                </div>

                <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-6">
                  <div>
                    <p className="text-xs text-white/40">Prix de base</p>
                    <p className="gold-text font-serif text-3xl">{euro(v.base_fare)}</p>
                    <p className="text-xs text-white/40">+ {euro(v.per_km)}/km · {euro(v.per_hour)}/h</p>
                  </div>
                  <Link
                    to={`/reservation?vehicle=${v.id}`}
                    data-testid={`fleet-choose-${v.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3 text-sm font-medium text-black transition-colors duration-300 hover:bg-goldsoft"
                  >
                    Choisir ce véhicule <ArrowRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </section>
    </div>
  );
}
